import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Trash2, ShieldAlert, CheckCircle2 } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useI18n } from "@/i18n/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

export default function DeleteAccount() {
  const { t } = useI18n();
  const { user, logout } = useAuth();
  const [creds, setCreds] = useState({ email: "", password: "" });
  const [sessionToken, setSessionToken] = useState(null);
  const [authedEmail, setAuthedEmail] = useState(user?.email || null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const isAuthed = !!user || !!sessionToken;
  const shownEmail = user?.email || authedEmail;

  const signin = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/login", creds);
      setSessionToken(data.token);
      setAuthedEmail(data.user?.email);
    } catch (err) {
      toast.error(apiError(err));
    }
    setBusy(false);
  };

  const doDelete = async () => {
    setBusy(true);
    try {
      const cfg = sessionToken ? { headers: { Authorization: `Bearer ${sessionToken}` } } : undefined;
      await api.delete("/auth/account", cfg);
      if (user) logout();
      setDone(true);
    } catch (err) {
      toast.error(apiError(err));
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="mx-auto max-w-2xl px-5 py-12 lg:py-16">
        <p className="label-eyebrow">{t("del.title")}</p>
        <h1 className="mt-2 font-display text-3xl tracking-tight sm:text-4xl" data-testid="delete-account-page-title">
          {t("del.subtitle")}
        </h1>
        <p className="mt-4 text-base text-slate-600" data-testid="delete-account-intro">{t("del.intro")}</p>

        <section className="mt-8 rounded-xl border border-slate-200 bg-white p-6 ambient">
          <p className="flex items-center gap-2 label-eyebrow">
            <ShieldAlert size={15} className="text-rose-600" /> {t("del.whatTitle")}
          </p>
          <ul className="mt-4 list-disc space-y-2 pl-5 text-sm text-slate-600">
            <li>{t("del.what1")}</li>
            <li>{t("del.what2")}</li>
            <li>{t("del.what3")}</li>
          </ul>
        </section>

        {done ? (
          <section className="mt-6 rounded-xl border border-emerald-200 bg-emerald-50/50 p-6 text-center" data-testid="delete-account-done">
            <CheckCircle2 size={32} className="mx-auto text-emerald-600" />
            <h2 className="mt-3 font-display text-2xl text-slate-900">{t("del.doneTitle")}</h2>
            <p className="mt-2 text-sm text-slate-600">{t("del.doneDesc")}</p>
            <Link to="/" data-testid="delete-account-home-link" className="mt-5 inline-block font-semibold text-sea underline">
              {t("del.backHome")}
            </Link>
          </section>
        ) : isAuthed ? (
          <section className="mt-6 rounded-xl border border-rose-200 bg-rose-50/40 p-6" data-testid="delete-account-confirm-section">
            <p className="text-sm text-slate-600">
              {t("del.loggedInAs")} <span className="font-semibold text-slate-900" data-testid="delete-account-email">{shownEmail}</span>
            </p>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  type="button"
                  disabled={busy}
                  data-testid="delete-account-btn"
                  className="mt-4 bg-rose-600 hover:bg-rose-700"
                >
                  <Trash2 size={15} className="mr-1.5" />
                  {busy ? t("del.deleting") : t("del.deleteBtn")}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent data-testid="delete-account-dialog">
                <AlertDialogHeader>
                  <AlertDialogTitle data-testid="delete-account-dialog-title">{t("del.confirmTitle")}</AlertDialogTitle>
                  <AlertDialogDescription>{t("del.confirmDesc")}</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel data-testid="delete-account-cancel-btn">{t("del.cancel")}</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={doDelete}
                    disabled={busy}
                    data-testid="delete-account-confirm-btn"
                    className="bg-rose-600 hover:bg-rose-700"
                  >
                    {busy ? t("del.deleting") : t("del.confirm")}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </section>
        ) : (
          <form onSubmit={signin} className="mt-6 rounded-xl border border-slate-200 bg-white p-6 ambient" data-testid="delete-account-signin-form">
            <p className="text-sm text-slate-600">{t("del.signinPrompt")}</p>
            <div className="mt-5 space-y-4">
              <div>
                <Label htmlFor="del-email">{t("del.email")}</Label>
                <Input id="del-email" type="email" required autoComplete="email" data-testid="delete-account-email-input"
                  value={creds.email} onChange={(e) => setCreds({ ...creds, email: e.target.value })} className="mt-1.5" />
              </div>
              <div>
                <Label htmlFor="del-pw">{t("del.password")}</Label>
                <Input id="del-pw" type="password" required autoComplete="current-password" data-testid="delete-account-password-input"
                  value={creds.password} onChange={(e) => setCreds({ ...creds, password: e.target.value })} className="mt-1.5" />
              </div>
            </div>
            <Button type="submit" disabled={busy} data-testid="delete-account-signin-btn" className="mt-6 w-full bg-sea hover:bg-sea-dark">
              {busy ? t("common.loading") : t("del.signin")}
            </Button>
          </form>
        )}
      </div>
      <Footer />
    </div>
  );
}
